import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { notificationsApi } from "@/lib/api/notifications";
import { qk } from "@/lib/query-client";
import { formatDateTime } from "@/lib/utils/format";
import { cn } from "@/lib/utils/cn";
import { useMountTransition } from "@/lib/utils/useMountTransition";
import { Icon } from "./Icon";
import { Spinner } from "@/components/ui/Spinner";

/** In-app notifications only - this codebase has no outbound-mail
 * infrastructure, so the bell is the entire delivery surface (see
 * NotificationService's doc comment on the backend). Polls the unread count
 * every 60s rather than opening a socket - the simplest thing that keeps the
 * badge honest without a new transport layer. */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const { shouldRender, isVisible } = useMountTransition(open, 150);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: unread } = useQuery({
    queryKey: qk.notifications.unreadCount,
    queryFn: notificationsApi.unreadCount,
    refetchInterval: 60_000,
  });

  const { data: page, isLoading } = useQuery({
    queryKey: qk.notifications.list({ per_page: 10 }),
    queryFn: () => notificationsApi.list({ per_page: 10 }),
    enabled: open,
  });

  const markAllRead = useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const markRead = useMutation({
    mutationFn: notificationsApi.markRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const count = unread?.count ?? 0;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-lg p-2 text-slate-500 transition-colors duration-200 ease-out hover:bg-primary-50 hover:text-primary-700"
        aria-label="Notifications"
      >
        <Icon name="bell" className="h-5 w-5" />
        {count > 0 && (
          // A restrained, existing-count-only pulse (Tailwind's gentle
          // opacity-cycle `animate-pulse`, not a scaling `ping` ring) - the
          // "genuinely necessary" case the interaction spec calls out
          // (unread attention), not applied anywhere else in the app. Pink,
          // not red - this is the app's one true "unread indicator," and
          // red is reserved for actual error/destructive semantics
          // elsewhere (StatusBadge's rejected/failed/etc tones), not for
          // "you have something to read."
          <span className="absolute right-1 top-1 flex h-4 min-w-[16px] animate-pulse items-center justify-center rounded-full bg-accent-600 px-1 text-[10px] font-semibold text-white">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {shouldRender && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            className={cn(
              "absolute right-0 z-20 mt-2 w-80 origin-top-right rounded-lg border border-slate-200 bg-white shadow-lg transition-all duration-150 ease-out",
              isVisible ? "translate-y-0 opacity-100" : "-translate-y-1 opacity-0",
            )}
          >
            <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
              <p className="text-sm font-medium text-slate-800">Notifications</p>
              {count > 0 && (
                <button
                  onClick={() => markAllRead.mutate()}
                  className="text-xs font-medium text-primary-700 hover:underline"
                >
                  Mark all read
                </button>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto">
              {isLoading && (
                <div className="flex justify-center py-6">
                  <Spinner className="h-5 w-5 text-primary-600" />
                </div>
              )}
              {!isLoading && (page?.items.length ?? 0) === 0 && (
                <p className="px-3 py-6 text-center text-sm text-slate-400">You're all caught up</p>
              )}
              {page?.items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => {
                    if (!n.is_read) markRead.mutate(n.id);
                    setOpen(false);
                    if (n.link) navigate(n.link);
                  }}
                  className={`block w-full border-b border-slate-50 px-3 py-2.5 text-left transition-colors duration-200 ease-out last:border-0 hover:bg-primary-50/60 ${
                    n.is_read ? "" : "bg-primary-50/50"
                  }`}
                >
                  <p className="text-sm font-medium text-slate-800">{n.title}</p>
                  {n.message && <p className="mt-0.5 truncate text-xs text-slate-500">{n.message}</p>}
                  <p className="mt-1 text-[11px] text-slate-400">{formatDateTime(n.created_at)}</p>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
